/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / SCHEDULE-PLAN — Manager-Maske Dienstplan (S263, Cut B)
   =============================================================================
   Eigener Topbar-Reiter „Dienstplan" (#admin/schedule/plan), manager+ (Spec § 6.2).
   Wochenraster: Zeilen Mo–So, Spalten 12–16 · 16–22 · 12–22, je Zelle beliebig
   viele Chips (Jonas: mehrere Personen je Zelle sind der Normalfall). Je Tag
   eine Spalte „Verfügbar" mit den noch nicht zugewiesenen Teilnehmern und ihrer
   Wahl — Klick auf `either`/`full` öffnet ein Mini-Popover mit den zulässigen
   Spalten, sonst geht es direkt in die eine Spalte.

   Daten kommen ausschließlich aus `schedule_week_overview` (eine Antwort).
   Schreiben nur über `schedule_assign` / `schedule_unassign` /
   `schedule_open_week` / `schedule_send_reminder` — die Verfügbarkeits-Matrix
   ist server-fest (SCHEDULE_NOT_AVAILABLE); hier wird sie nur zur Anzeige
   gespiegelt (core/schedule-shifts.js).

   Rendering ist non-destruktiv: einmal das Gerüst, danach werden nur Zellen
   patcht, deren Markup sich geändert hat (Key → letztes HTML). Realtime auf
   `schedule_assignments` läuft über die Live-View-Registry ('schedule:realtime',
   beim Boot in realtime.js abonniert): sichtbar → Reload + Patch, sonst dirty.
   Inline-Handler als func() ohne window. (dom-bindings).
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { registerLiveView } from '../../core/live-view-registry.js';
import {
  SHIFT_CODES, SHIFT_LABELS, CHOICE_LABELS, columnsForChoice, isValidChoice,
  weekDays, isoDateAdd, isoDow, isoWeekNumber, formatDayShort, formatWeekRange, formatDeadline,
} from '../../core/schedule-shifts.js';
// S264 Cut C — Veröffentlichen (PDF + Mail), Vorschau, PDF öffnen.
import {
  publishSchedule, openSchedulePreview, openSchedulePdf, publishRecipients, isEmptyPlan,
} from './schedule-publish.js';

const _state = {
  weekStart: null,   // 'YYYY-MM-DD' (Montag)
  data: null,        // schedule_week_overview-Antwort
  loading: false,
  error: null,
  popover: null,     // { day, userId } | null
  busy: false,       // laufende Mutation
  mounted: false,
};
const _last = new Map();        // Patch-Guard: key → zuletzt geschriebenes Markup
let _reloadTimer = null;
let _docClickBound = false;
let _liveRegistered = false;

/* -------------------------------------------------------------------------- */
/* Pure Helfer (Vitest)                                                        */
/* -------------------------------------------------------------------------- */

/** Montag der Woche, in der `iso` liegt. */
export function mondayOf(iso) {
  const dow = isoDow(iso);
  if (!dow) return null;
  return isoDateAdd(iso, -(dow - 1));
}

/** Default-Woche der Maske: ab Donnerstag die Folgewoche (dann läuft die Abfrage),
 *  Mo–Mi die laufende Woche. */
export function defaultWeekStart(todayIso) {
  const mon = mondayOf(todayIso);
  if (!mon) return null;
  return (isoDow(todayIso) >= 4) ? isoDateAdd(mon, 7) : mon;
}

export function shiftWeek(weekStart, n) {
  return isoDateAdd(weekStart, 7 * (Number(n) || 0));
}

/** Zuweisungen je Zelle: Map 'day|shift' → [assignment]. */
export function assignmentsByCell(assignments) {
  const m = new Map();
  for (const a of (Array.isArray(assignments) ? assignments : [])) {
    if (!a || !a.day || !a.shift) continue;
    const k = a.day + '|' + a.shift;
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(a);
  }
  return m;
}

/** Wer ist an einem Tag schon zugewiesen (user_id → assignment)? */
export function assignedOnDay(assignments, day) {
  const m = new Map();
  for (const a of (Array.isArray(assignments) ? assignments : [])) {
    if (a && a.day === day) m.set(a.user_id, a);
  }
  return m;
}

/**
 * Verfügbare Teilnehmer eines Tages (noch nicht zugewiesen, Wahl deckt mindestens
 * eine Spalte) und die Nicht-Verfügbaren (none / keine Angabe) — beide Listen
 * getrennt, damit die Maske die zweite ausgegraut zeigen kann.
 */
export function availabilityForDay(participants, assignments, day) {
  const taken = assignedOnDay(assignments, day);
  const available = [];
  const unavailable = [];
  for (const p of (Array.isArray(participants) ? participants : [])) {
    if (!p || !p.user_id) continue;
    if (taken.has(p.user_id)) continue;
    const choice = (p.choices && p.choices[day]) || null;
    const cols = columnsForChoice(choice);
    if (isValidChoice(choice) && cols.length) available.push({ p, choice, cols });
    else unavailable.push({ p, choice: isValidChoice(choice) ? choice : null });
  }
  return { available, unavailable };
}

function _fmtDayTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) + ' '
    + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

/** Status-Chip der Woche. S264: „Veröffentlicht v{n} · dd.mm. hh:mm". */
export function statusChip(week, nowMs) {
  if (!week) return { cls: 'is-none', text: 'Noch keine Abfrage' };
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const dl = week.deadline_at ? new Date(week.deadline_at).getTime() : NaN;
  if (week.status === 'published') {
    const when = _fmtDayTime(week.published_at);
    return { cls: 'is-published', text: `Veröffentlicht v${week.published_version || 1}` + (when ? ` · ${when}` : '') };
  }
  if (week.status === 'closed' || (Number.isFinite(dl) && now >= dl)) return { cls: 'is-closed', text: 'Geschlossen — verteilen' };
  return { cls: 'is-collecting', text: 'Sammeln bis ' + formatDeadline(week.deadline_at) };
}

/**
 * S264/S265 — Zustand des Veröffentlichen-Knopfs (pure).
 *   enabled  Woche existiert
 *   label    „Veröffentlichen" bzw. „Erneut veröffentlichen (v{n+1})"
 *   changed  Zuweisungen seit v{n} geändert (Server: changed_since_publish)
 *   primary  O3: primär nur vor dem ersten Publish oder MIT Änderungen; ein
 *            erneutes Senden ohne Änderungen ist sekundär (kein Handlungsbedarf)
 *   title    Hinweistext für den Knopf (nRecip = Empfängerzahl)
 */
export function publishState(week, nRecip) {
  const n = Number(nRecip) || 0;
  const to = n ? ` an ${n} Teilnehmer` : '';
  if (!week) {
    return { enabled: false, label: '📤 Veröffentlichen', changed: false, primary: true,
      title: 'Zuerst die Abfrage senden — die Woche existiert noch nicht.' };
  }
  const v = Number(week.published_version) || 0;
  if (v === 0) {
    return { enabled: true, label: '📤 Veröffentlichen', changed: false, primary: true,
      title: `Plan als Mail + PDF${to}` };
  }
  const changed = week.changed_since_publish === true;
  return {
    enabled: true,
    label: `📤 Erneut veröffentlichen (v${v + 1})`,
    changed,
    primary: changed,
    title: changed
      ? `Zuweisungen haben sich seit v${v} geändert — erneut${to} mailen (mit PDF)`
      : `Keine Änderungen seit v${v} — erneut senden schickt denselben Plan an alle`,
  };
}

export function participantById(participants, id) {
  return (Array.isArray(participants) ? participants : []).find(p => p && p.user_id === id) || null;
}

function _hhmm(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function _firstName(name) {
  return String(name || '').trim().split(/\s+/)[0] || '—';
}

/** Chip einer zugewiesenen Person in einer Zelle (Initialen + Vorname, ✕). */
export function _assignedChipHtml(a, p, day, locked) {
  const ini = htmlEscape((p && p.initials) || '??');
  const name = htmlEscape(_firstName(p && p.name));
  const uid = htmlEscape(a.user_id);
  const x = locked ? '' : `<button type="button" class="sp-chip-x" title="Zuweisung entfernen" aria-label="Zuweisung entfernen"
        onclick="spUnassign('${htmlEscape(day)}', '${uid}')">✕</button>`;
  return `<span class="sp-chip" data-sp-user="${uid}"><b>${ini}</b> ${name}${x}</span>`;
}

/** Eine Raster-Zelle (Tag × Schicht). */
export function _cellHtml(day, shift, list, participants, locked) {
  const chips = (list || []).map(a => _assignedChipHtml(a, participantById(participants, a.user_id), day, locked)).join('');
  return chips || '<span class="sp-empty">–</span>';
}

/** Die Verfügbar-Spalte eines Tages inkl. offenem Popover. */
export function _availHtml(day, avail, popover, locked) {
  const { available, unavailable } = avail;
  const parts = [];
  for (const { p, choice, cols } of available) {
    const uid = htmlEscape(p.user_id);
    const open = popover && popover.day === day && popover.userId === p.user_id;
    const label = `<b>${htmlEscape(p.initials || '??')}</b> ${htmlEscape(_firstName(p.name))} <small>${htmlEscape(CHOICE_LABELS[choice] || choice)}</small>`;
    if (locked) {
      parts.push(`<span class="sp-avail is-static">${label}</span>`);
      continue;
    }
    if (cols.length === 1) {
      parts.push(`<button type="button" class="sp-avail" title="Zuweisen: ${htmlEscape(SHIFT_LABELS[cols[0]])}"
        onclick="spAssign('${htmlEscape(day)}', '${uid}', '${cols[0]}')">${label}</button>`);
    } else {
      const pop = open
        ? `<span class="sp-pop" role="menu">${cols.map(c =>
            `<button type="button" class="sp-pop-btn" onclick="spAssign('${htmlEscape(day)}', '${uid}', '${c}')">${htmlEscape(SHIFT_LABELS[c])}</button>`
          ).join('')}<button type="button" class="sp-pop-x" aria-label="Schließen" onclick="spClosePopover()">✕</button></span>`
        : '';
      parts.push(`<span class="sp-avail-wrap"><button type="button" class="sp-avail${open ? ' is-open' : ''}" aria-haspopup="true" aria-expanded="${open ? 'true' : 'false'}"
        onclick="spPick('${htmlEscape(day)}', '${uid}')">${label} ▾</button>${pop}</span>`);
    }
  }
  for (const { p, choice } of unavailable) {
    parts.push(`<span class="sp-avail is-off" title="${choice === 'none' ? 'nicht verfügbar' : 'keine Angabe'}"><b>${htmlEscape(p.initials || '??')}</b> ${htmlEscape(_firstName(p.name))} <small>${choice === 'none' ? 'nicht verfügbar' : 'keine Angabe'}</small></span>`);
  }
  return parts.join('') || '<span class="sp-empty">niemand</span>';
}

/** Kopfzeile: Woche, Status, Knöpfe. */
export function _headHtml(data, weekStart, busy) {
  const week = data && data.week;
  const chip = statusChip(week);
  const parts = (data && Array.isArray(data.participants)) ? data.participants : [];
  const nParticipants = parts.filter(p => p && p.participant && p.active).length;
  const nOpen = week ? parts.filter(p => p && p.request_id && !p.submitted_at).length : 0;
  const collecting = !!week && week.status === 'collecting';
  const reqTitle = week && week.request_sent_at
    ? `Abfrage bereits gesendet um ${_hhmm(week.request_sent_at)} — erneut sendet nur an neue Teilnehmer`
    : 'Woche anlegen + Abfrage-Mail an alle Teilnehmer';
  const remTitle = week && week.reminder_sent_at
    ? `Erinnerung bereits gesendet um ${_hhmm(week.reminder_sent_at)} — erneut erreicht nur bisher nicht Erinnerte`
    : 'Erinnerung an alle, die noch nichts eingetragen haben';
  // S264 — Veröffentlichen / Vorschau / PDF.
  const nRecip = publishRecipients(parts).length;
  const pub = publishState(week, nRecip);
  const pubTitle = pub.title;
  // O2 (S264): sichtbare Hinweiszeile statt nur Title — „Abfrage gesendet Do 14.09. 10:00 · erneut = nur an Neue".
  const hints = [];
  if (week && week.request_sent_at) hints.push(`✉️ Abfrage gesendet ${_fmtDayTime(week.request_sent_at)} · erneut = nur an Neue`);
  if (week && week.reminder_sent_at) hints.push(`🔔 Erinnerung gesendet ${_fmtDayTime(week.reminder_sent_at)} · erneut = nur an noch nicht Erinnerte`);
  const hintHtml = hints.length ? `<div class="sp-hint">${hints.map(h => htmlEscape(h)).join(' &nbsp;·&nbsp; ')}</div>` : '';
  return `
    <div class="sp-head">
      <div class="sp-nav">
        <button type="button" class="btn btn-ghost btn-sm" onclick="spPrevWeek()" aria-label="Vorige Woche">‹</button>
        <div class="sp-week">
          <div class="sp-week-kw">KW ${isoWeekNumber(weekStart)}</div>
          <div class="sp-week-range">${htmlEscape(formatWeekRange(weekStart))}</div>
        </div>
        <button type="button" class="btn btn-ghost btn-sm" onclick="spNextWeek()" aria-label="Nächste Woche">›</button>
        <button type="button" class="btn btn-ghost btn-sm" onclick="spThisWeek()">Aktuell</button>
        <span class="sp-status ${chip.cls}">${htmlEscape(chip.text)}</span>
        ${pub.changed ? `<span class="sp-status is-changed" title="Seit der letzten Veröffentlichung wurden Zuweisungen geändert">Änderungen seit v${week.published_version}</span>` : ''}
        ${data && data.cycle_enabled === false ? '<span class="sp-cycle-off" title="Der automatische Zyklus ist in Einstellungen → Team ausgeschaltet">Zyklus aus</span>' : ''}
      </div>
      <div class="sp-actions">
        <button type="button" class="btn btn-secondary btn-sm" onclick="spSendRequest()" ${busy ? 'disabled' : ''} title="${htmlEscape(reqTitle)}">
          ✉️ Abfrage jetzt senden${nParticipants ? ` (${nParticipants})` : ''}
        </button>
        <button type="button" class="btn btn-secondary btn-sm" onclick="spSendReminder()" ${(!collecting || busy) ? 'disabled' : ''} title="${htmlEscape(remTitle)}">
          🔔 Erinnerung senden${collecting ? ` (${nOpen} offen)` : ''}
        </button>
        <button type="button" class="btn btn-ghost btn-sm" onclick="spPreviewPlan()" ${(!week || busy) ? 'disabled' : ''} title="Den Plan als Seite ansehen (drucken möglich)">
          👁 Plan ansehen
        </button>
        <button type="button" class="btn btn-ghost btn-sm" onclick="spOpenPdf()" ${(!week || !week.pdf_path || busy) ? 'disabled' : ''} title="${week && week.pdf_path ? 'Das PDF der letzten Version öffnen' : 'Noch kein PDF — zuerst veröffentlichen'}">
          📄 PDF öffnen
        </button>
        <button type="button" class="btn ${pub.primary ? 'btn-primary' : 'btn-secondary'} btn-sm${pub.changed ? ' is-attention' : ''}" onclick="spPublish()" ${(!pub.enabled || busy) ? 'disabled' : ''} title="${htmlEscape(pubTitle)}">
          ${htmlEscape(pub.label)}
        </button>
      </div>
      ${hintHtml}
      <div class="sp-progress" id="sp-progress" aria-live="polite"></div>
    </div>`;
}

/* -------------------------------------------------------------------------- */
/* Infrastruktur                                                               */
/* -------------------------------------------------------------------------- */

function _supa() { return (window.App && window.App.supabase) || null; }
function _sid() {
  try {
    const op = window.App && window.App.operator;
    return (op && typeof op.getActiveSessionId === 'function') ? op.getActiveSessionId() : null;
  } catch (_) { return null; }
}
function _root() { return document.getElementById('tab-plan'); }
function _todayIso() {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

export function _errorCode(err) {
  const msg = (err && (err.message || err.details)) || (typeof err === 'string' ? err : '');
  const m = String(msg || '').match(/^\s*([A-Z_]{4,})\s*:/);
  return m ? m[1] : null;
}
function _errorText(err) {
  const msg = String((err && err.message) || err || '');
  const i = msg.indexOf(':');
  return i > 0 ? msg.slice(i + 1).trim() : msg;
}

async function _rpc(fn, args) {
  const supa = _supa();
  if (!supa) throw new Error('Verbindung nicht bereit.');
  const { data, error } = await supa.rpc(fn, args);
  if (error) throw error;
  return data;
}

function _patch(key, el, html) {
  if (!el) return;
  if (_last.get(key) === html) return;
  _last.set(key, html);
  el.innerHTML = html;
}

/* -------------------------------------------------------------------------- */
/* Laden + Rendern                                                             */
/* -------------------------------------------------------------------------- */

async function _load() {
  if (!_state.weekStart) return;
  _state.loading = true;
  const ws = _state.weekStart;
  try {
    const data = await _rpc('schedule_week_overview', { p_week_start: ws, p_operator_session_id: _sid() });
    if (ws !== _state.weekStart) return;   // Woche inzwischen gewechselt
    _state.data = data;
    _state.error = null;
  } catch (e) {
    console.warn('[schedule-plan] schedule_week_overview:', _errorCode(e) || (e && e.message));
    _state.error = e;
    _state.data = null;
  } finally {
    _state.loading = false;
  }
  _render();
}

function _reloadSoon() {
  clearTimeout(_reloadTimer);
  _reloadTimer = setTimeout(() => { _load().catch(() => {}); }, 250);
}

function _shellHtml() {
  return `
    <div class="sp-wrap">
      <div id="sp-head"></div>
      <div id="sp-body"></div>
    </div>`;
}

function _gridSkeleton(days) {
  const rows = days.map(d => `
    <tr data-sp-row="${htmlEscape(d)}">
      <th scope="row" class="sp-day">${htmlEscape(formatDayShort(d))}</th>
      ${SHIFT_CODES.map(s => `<td class="sp-cell" data-sp-cell="${htmlEscape(d)}|${s}"></td>`).join('')}
      <td class="sp-avail-cell" data-sp-avail="${htmlEscape(d)}"></td>
    </tr>`).join('');
  return `
    <div class="sp-grid-scroll">
      <table class="sp-grid">
        <thead><tr>
          <th>Tag</th>
          ${SHIFT_CODES.map(s => `<th>${htmlEscape(SHIFT_LABELS[s])}</th>`).join('')}
          <th>Verfügbar</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

function _render() {
  const root = _root();
  if (!root || !_state.mounted) return;
  const head = document.getElementById('sp-head');
  const body = document.getElementById('sp-body');
  if (!head || !body) return;

  _patch('head', head, _headHtml(_state.data, _state.weekStart, _state.busy));

  if (_state.error) {
    const code = _errorCode(_state.error);
    const txt = code === 'OPERATOR_REQUIRED' || code === 'OPERATOR_FORBIDDEN' || code === 'NICHT_BERECHTIGT'
      ? _errorText(_state.error)
      : 'Die Woche konnte nicht geladen werden.';
    _patch('body', body, `<div class="card"><p style="color:var(--danger);">${htmlEscape(txt)}</p>
      <button type="button" class="btn btn-secondary btn-sm" onclick="spReload()">Erneut laden</button></div>`);
    _last.delete('grid');
    return;
  }
  if (!_state.data) {
    _patch('body', body, '<div class="card"><p class="hint">Woche wird geladen …</p></div>');
    _last.delete('grid');
    return;
  }

  const d = _state.data;
  const days = (Array.isArray(d.days) && d.days.length === 7) ? d.days : weekDays(_state.weekStart);
  const week = d.week;
  const locked = false;   // Cut C: nach Veröffentlichung weiter editierbar (v+1) — kein Lock in V1
  const gridKey = 'grid:' + _state.weekStart;

  if (_last.get('grid') !== gridKey) {
    // Neue Woche (oder erster Render): Gerüst neu, Zell-Cache leeren.
    for (const k of Array.from(_last.keys())) if (k.startsWith('cell:') || k.startsWith('avail:') || k === 'note') _last.delete(k);
    body.innerHTML = `<div id="sp-note"></div>${_gridSkeleton(days)}`;
    _last.set('grid', gridKey);
    _last.set('body', null);
  }

  const parts = Array.isArray(d.participants) ? d.participants : [];
  const noteHtml = !week
    ? `<p class="hint sp-note">Für diese Woche gibt es noch keine Abfrage. „Abfrage jetzt senden" legt die Woche an und schickt allen ${parts.filter(p => p && p.participant && p.active).length} Teilnehmern ihren Link.</p>`
    : (parts.length === 0
      ? '<p class="hint sp-note">Keine Teilnehmer — im Team-Tab den Dienstplan-Schalter setzen.</p>'
      : '');
  _patch('note', document.getElementById('sp-note'), noteHtml);

  const byCell = assignmentsByCell(d.assignments);
  for (const day of days) {
    for (const s of SHIFT_CODES) {
      const key = day + '|' + s;
      _patch('cell:' + key, body.querySelector(`[data-sp-cell="${key}"]`), _cellHtml(day, s, byCell.get(key), parts, locked));
    }
    const avail = availabilityForDay(parts, d.assignments, day);
    const pop = (_state.popover && _state.popover.day === day) ? _state.popover : null;
    _patch('avail:' + day, body.querySelector(`[data-sp-avail="${day}"]`), _availHtml(day, avail, pop, locked));
  }
}

/* -------------------------------------------------------------------------- */
/* Einstieg + Handler                                                          */
/* -------------------------------------------------------------------------- */

/** Tab-Renderer (view-switch map: plan → renderSchedulePlan). */
export function renderSchedulePlan() {
  const root = _root();
  if (!root) return;
  if (!_state.mounted) {
    root.innerHTML = _shellHtml();
    _state.mounted = true;
    _last.clear();
    if (!_state.weekStart) _state.weekStart = defaultWeekStart(_todayIso());
    if (!_liveRegistered) {
      _liveRegistered = true;
      registerLiveView({ tabId: 'tab-plan', render: _reloadSoon, events: ['schedule:realtime'] });
    }
    if (!_docClickBound) {
      _docClickBound = true;
      document.addEventListener('click', (ev) => {
        if (!_state.popover) return;
        const t = ev.target;
        if (t && t.closest && (t.closest('.sp-pop') || t.closest('.sp-avail-wrap'))) return;
        spClosePopover();
      });
      // O1 (S264): zurück in den Tab / Fenster fokussiert → frisch laden. Realtime
      // deckt Abgaben und Wochen-Status seit phase_195 ab; das hier ist der Gurt
      // für einen Tab, der stundenlang im Hintergrund lag.
      const refetchIfVisible = () => {
        if (document.visibilityState !== 'visible') return;
        const root = _root();
        if (root && root.style.display !== 'none' && _state.mounted) _reloadSoon();
      };
      document.addEventListener('visibilitychange', refetchIfVisible);
      window.addEventListener('focus', refetchIfVisible);
    }
  }
  _render();
  _load().catch(() => {});
}

/* -------------------------------------------------------------------------- */
/* S264 — Veröffentlichen / Vorschau / PDF                                     */
/* -------------------------------------------------------------------------- */

function _progress(text) {
  const el = document.getElementById('sp-progress');
  if (el) el.textContent = text || '';
}

export async function spPublish() {
  const d = _state.data;
  const week = d && d.week;
  if (!week) { showToast('Zuerst die Abfrage senden — die Woche existiert noch nicht.', 'info'); return; }
  if (_state.busy) return;
  const nextV = (Number(week.published_version) || 0) + 1;
  const recips = publishRecipients(d.participants).length;
  if (isEmptyPlan(d.assignments)) {
    const r0 = await window.styledConfirm({
      title: 'Leerer Plan — trotzdem senden?',
      body: `Für KW ${isoWeekNumber(_state.weekStart)} ist niemand zugewiesen. Die Mail an ${recips} Teilnehmer würde einen leeren Plan zeigen.`,
      confirmLabel: 'Trotzdem senden',
    });
    if (!r0 || !r0.confirmed) return;
  }
  const res = await window.styledConfirm({
    title: nextV === 1 ? 'Dienstplan veröffentlichen?' : `Dienstplan erneut veröffentlichen (v${nextV})?`,
    body: `Dienstplan KW ${isoWeekNumber(_state.weekStart)} als v${nextV} an ${recips} Teilnehmer mailen (mit PDF)?`
      + (nextV > 1 ? ' Alle bekommen den kompletten Plan erneut.' : ''),
    confirmLabel: 'Veröffentlichen',
  });
  if (!res || !res.confirmed) return;
  return _mutate('schedule_publish', async () => {
    const r = await publishSchedule(d, { sessionId: _sid(), onProgress: _progress });
    _progress('');
    const mails = Number(r && r.mails_enqueued) || 0;
    if (r && r.kind_enabled === false) {
      showToast(`Plan als v${r.version} veröffentlicht — Mail-Art „Dienstplan: Plan veröffentlicht" ist ausgeschaltet, keine Mail.`, 'info');
    } else {
      showToast(`Dienstplan v${r.version} an ${mails} Teilnehmer gesendet (PDF im Anhang).`, 'success');
    }
  }).finally(() => _progress(''));
}

export function spPreviewPlan() {
  const d = _state.data;
  if (!d || !d.week) { showToast('Zuerst die Abfrage senden.', 'info'); return; }
  openSchedulePreview(d).catch(e => showToast('Vorschau fehlgeschlagen: ' + (e.message || e), 'error'));
}

export function spOpenPdf() {
  openSchedulePdf(_state.data);
}

export function spReload() { _load().catch(() => {}); }

export function spPrevWeek() { _setWeek(shiftWeek(_state.weekStart, -1)); }
export function spNextWeek() { _setWeek(shiftWeek(_state.weekStart, 1)); }
export function spThisWeek() { _setWeek(defaultWeekStart(_todayIso())); }

function _setWeek(ws) {
  if (!ws || ws === _state.weekStart) return;
  _state.weekStart = ws;
  _state.popover = null;
  _state.data = null;
  _state.error = null;
  _render();
  _load().catch(() => {});
}

/** Klick auf einen Verfügbar-Chip mit mehreren zulässigen Spalten → Popover. */
export function spPick(day, userId) {
  const open = _state.popover && _state.popover.day === day && _state.popover.userId === userId;
  _state.popover = open ? null : { day, userId };
  _render();
}

export function spClosePopover() {
  if (!_state.popover) return;
  _state.popover = null;
  _render();
}

async function _mutate(label, fn) {
  if (_state.busy) return;
  _state.busy = true;
  _render();
  try {
    await fn();
  } catch (e) {
    const code = _errorCode(e);
    console.warn('[schedule-plan] ' + label + ':', code || (e && e.message));
    if (code === 'SCHEDULE_NOT_AVAILABLE') showToast('Nicht verfügbar: ' + _errorText(e), 'error');
    else if (code === 'SCHEDULE_LOCKED') showToast(_errorText(e) || 'Die Woche ist gesperrt.', 'error');
    else if (code === 'OPERATOR_REQUIRED' || code === 'OPERATOR_FORBIDDEN' || code === 'NICHT_BERECHTIGT') showToast(_errorText(e), 'error');
    else showToast('Das hat gerade nicht geklappt: ' + _errorText(e), 'error');
  } finally {
    _state.busy = false;
    _state.popover = null;
    await _load().catch(() => {});
  }
}

export function spAssign(day, userId, shift) {
  const week = _state.data && _state.data.week;
  if (!week) { showToast('Zuerst die Abfrage senden — die Woche existiert noch nicht.', 'info'); return; }
  return _mutate('schedule_assign', async () => {
    await _rpc('schedule_assign', {
      p_week_id: week.id, p_user_id: userId, p_day: day, p_shift: shift, p_operator_session_id: _sid(),
    });
  });
}

export function spUnassign(day, userId) {
  const week = _state.data && _state.data.week;
  if (!week) return;
  return _mutate('schedule_unassign', async () => {
    await _rpc('schedule_unassign', {
      p_week_id: week.id, p_user_id: userId, p_day: day, p_operator_session_id: _sid(),
    });
  });
}

export async function spSendRequest() {
  const d = _state.data;
  const n = d ? (d.participants || []).filter(p => p && p.participant && p.active).length : 0;
  const week = d && d.week;
  const body = week && week.request_sent_at
    ? `Die Abfrage für KW ${isoWeekNumber(_state.weekStart)} ging bereits um ${_hhmm(week.request_sent_at)} raus. Erneut senden erreicht nur Teilnehmer, die seitdem dazugekommen sind.`
    : `Woche KW ${isoWeekNumber(_state.weekStart)} anlegen und ${n} Teilnehmern den Link mailen?`;
  const res = await window.styledConfirm({ title: 'Abfrage jetzt senden?', body, confirmLabel: 'Senden' });
  if (!res || !res.confirmed) return;
  return _mutate('schedule_open_week', async () => {
    const r = await _rpc('schedule_open_week', { p_week_start: _state.weekStart, p_operator_session_id: _sid() });
    const mails = Number(r && r.mails_enqueued) || 0;
    if (mails > 0) showToast(`Abfrage an ${mails} Teilnehmer gesendet.`, 'success');
    else if (week && week.request_sent_at) showToast(`Bereits gesendet um ${_hhmm(week.request_sent_at)} — niemand Neues.`, 'info');
    else showToast('Woche angelegt — keine Mail (kein Teilnehmer mit E-Mail oder Mail-Art ausgeschaltet).', 'info');
  });
}

export async function spSendReminder() {
  const d = _state.data;
  const week = d && d.week;
  if (!week) { showToast('Zuerst die Abfrage senden.', 'info'); return; }
  const open = (d.participants || []).filter(p => p && p.request_id && !p.submitted_at).length;
  const body = week.reminder_sent_at
    ? `Die Erinnerung ging bereits um ${_hhmm(week.reminder_sent_at)} raus. Erneut senden erreicht nur, wer seitdem noch nicht erinnert wurde.`
    : `Erinnerung an ${open} Teilnehmer, die noch nichts eingetragen haben?`;
  const res = await window.styledConfirm({ title: 'Erinnerung senden?', body, confirmLabel: 'Senden' });
  if (!res || !res.confirmed) return;
  return _mutate('schedule_send_reminder', async () => {
    const r = await _rpc('schedule_send_reminder', { p_week_id: week.id, p_operator_session_id: _sid() });
    const n = Number(r && r.enqueued) || 0;
    if (n > 0) showToast(`Erinnerung an ${n} Teilnehmer gesendet.`, 'success');
    else if (r && r.reminder_sent_at) showToast(`Bereits gesendet um ${_hhmm(r.reminder_sent_at)} — niemand Neues.`, 'info');
    else showToast('Niemand zu erinnern.', 'info');
  });
}

/** Test-Hook. */
export function _resetSchedulePlanForTests() {
  _state.weekStart = null; _state.data = null; _state.loading = false; _state.error = null;
  _state.popover = null; _state.busy = false; _state.mounted = false;
  _last.clear();
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderSchedulePlan,
    spReload, spPrevWeek, spNextWeek, spThisWeek,
    spPick, spClosePopover, spAssign, spUnassign,
    spSendRequest, spSendReminder,
    // S264 Cut C
    spPublish, spPreviewPlan, spOpenPdf,
  });
}
